import {
  Alert,
  Autocomplete,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import * as React from "react";

import { useDomainServices } from "@/providers/DomainServices";
import {
  type FileSystemNode,
  selectedDirectoriesToPatterns,
  selectedItemsToFileIds,
} from "@/shared/domain/files/filesystem";
import { type SessionRecord } from "@/shared/domain/sessions";
import {
  useAddFilesToSession,
  useDatasetSessions,
} from "@/shared/domain/sessions/hooks";
import { Button } from "@/shared/foundation/primitives";

import styles from "./AddToSessionDialog.module.css";

interface AddToSessionDialogProps {
  open: boolean;
  onClose: () => void;
  datasetId: string;
  /**
   * The file-browser selection; selected directories are resolved to their
   * member files on submit.
   */
  selectedItems: FileSystemNode[];
  /**
   * Org that owns the target session — always required. Forwarded as the
   * resource-owner header on the add-files call.
   */
  resourceOwnerId: string;
  /** Called after files are successfully added, with the target session id. */
  onAdded: (sessionId: string) => void;
}

function sessionLabel(session: SessionRecord): string {
  return session.name
    ? `${session.name} (${session.session_id})`
    : session.session_id;
}

/**
 * Dialog that adds a file-browser selection to an existing session in the
 * dataset. The user picks from the dataset's sessions; on submit, selected
 * directories are resolved to file ids and the union with explicitly-selected
 * files is attached, each file contributing its entire time window. Success
 * feedback (and any navigation to the session) is the caller's responsibility
 * via `onAdded`.
 */
export function AddToSessionDialog({
  open,
  onClose,
  datasetId,
  selectedItems,
  resourceOwnerId,
  onAdded,
}: AddToSessionDialogProps) {
  const { datasetService } = useDomainServices();
  const {
    sessions,
    isLoading,
    error: listError,
  } = useDatasetSessions(datasetId);
  const addFilesMutation = useAddFilesToSession();

  const [selectedSession, setSelectedSession] =
    React.useState<SessionRecord | null>(null);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  // Spans the whole submit chain — including the pre-mutation directory
  // resolution, which pages through the dataset and can be slow — so the
  // submit button reads busy (and blocks re-entry) from first click until
  // the submission settles.
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  // Bumped on every close. An in-flight submission captures the value when it
  // starts and abandons itself once they differ: a dismissed submission must
  // not surface a stale error on the dialog's next open, nor fire `onAdded`
  // for an action the user walked away from.
  const openGenerationRef = React.useRef(0);

  const handleClose = () => {
    openGenerationRef.current += 1;
    setSelectedSession(null);
    setSubmitError(null);
    onClose();
  };

  const handleAdd = async () => {
    if (selectedSession === null || isSubmitting) {
      return;
    }
    const generation = openGenerationRef.current;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      // Explicitly-selected files are already known by id; only selected
      // directories need resolving, deferred to submit so a large selection
      // isn't enumerated until the user commits.
      const fileIds = selectedItemsToFileIds(selectedItems);
      const directoryPatterns = selectedDirectoriesToPatterns(selectedItems);
      const fromDirectories =
        directoryPatterns.length > 0
          ? await datasetService.listAllFiles(datasetId, {
              includePatterns: directoryPatterns,
            })
          : [];
      if (openGenerationRef.current !== generation) {
        return;
      }

      // A file can be both explicitly selected and inside a selected
      // directory, so union through a Set.
      const resolvedFileIds = new Set([
        ...fileIds,
        ...fromDirectories.map((file) => file.file_id),
      ]);

      if (resolvedFileIds.size === 0) {
        setSubmitError("No files matched the current selection.");
        return;
      }
      await addFilesMutation.mutateAsync({
        sessionId: selectedSession.session_id,
        files: Array.from(resolvedFileIds, (fileId) => ({ file_id: fileId })),
        resourceOwnerId,
      });
      if (openGenerationRef.current !== generation) {
        return;
      }
      handleClose();
      onAdded(selectedSession.session_id);
    } catch (error) {
      if (openGenerationRef.current !== generation) {
        return;
      }
      // Swallow the failure and surface it inline; the dialog stays open so
      // the user can retry without re-picking the session.
      setSubmitError(
        error instanceof Error
          ? error.message
          : "Failed to add files to the session. Please retry.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Add to existing session</DialogTitle>
      <DialogContent className={styles.content}>
        <Autocomplete<SessionRecord>
          options={sessions}
          loading={isLoading}
          value={selectedSession}
          onChange={(_event, value) => setSelectedSession(value)}
          getOptionLabel={sessionLabel}
          isOptionEqualToValue={(option, value) =>
            option.session_id === value.session_id
          }
          // MUI shows this whenever the *filtered* option list is empty, so
          // it must not claim the dataset has no sessions when typed text
          // merely matched none of them.
          noOptionsText={
            sessions.length === 0
              ? "No sessions in this dataset"
              : "No matching sessions"
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Session"
              placeholder="Select a session"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? (
                      <CircularProgress color="inherit" size={16} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <ListErrorAlert error={listError} />
        <SubmitErrorAlert message={submitError} />
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          loading={isSubmitting}
          disabled={selectedSession === null || isSubmitting}
          onClick={() => {
            void handleAdd();
          }}
        >
          Add files
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ListErrorAlert({ error }: { error: Error | null }) {
  if (error === null) {
    return null;
  }
  return (
    <Alert severity="error">Failed to load sessions: {error.message}</Alert>
  );
}

function SubmitErrorAlert({ message }: { message: string | null }) {
  if (message === null) {
    return null;
  }
  return <Alert severity="error">{message}</Alert>;
}
